import React, { useEffect, useState, useLayoutEffect, useCallback } from "react";
import { X, ChevronRight, Check, Ticket } from "lucide-react";

const IMG = "/assets/tutorial";

const DATA = {
  client: {
    flag: "kn_tutorial_client_v1",
    slides: [
      { icon: "👋", title: "Benvenuto su KoupoNow", text: "Scopri gli sconti dei negozi vicino a te, aggiornati ogni giorno.", img: `${IMG}/client_1.jpg` },
      { icon: "🔍", title: "Trova le offerte", text: "Cerca un negozio, filtra per categoria e ordina per distanza o sconto.", img: `${IMG}/client_2.jpg` },
      { icon: "📱", title: "Richiedi il coupon", text: "Salva le offerte e mostra il QR o il codice in cassa per ottenere lo sconto.", img: `${IMG}/client_3.jpg` },
      { icon: "🐷", title: "Segui e risparmia", text: "Segui i tuoi negozi preferiti e tieni d'occhio quanto hai risparmiato.", img: `${IMG}/client_4.jpg` },
    ],
    coach: [
      { sel: ["[data-testid=store-search-input]"], title: "Cerca qui", text: "Trova un negozio o un'offerta. Se un negozio non c'è ancora, puoi invitarlo!" },
      { sel: ["[data-testid=location-button]"], title: "La tua zona", text: "Imposta la tua posizione e il raggio per vedere le offerte più vicine." },
      { sel: ["[data-testid=open-filters]"], title: "Filtra per categoria", text: "Tocca qui per scegliere le categorie che ti interessano." },
      { sel: ["[data-testid=open-map-btn]"], title: "Esplora sulla mappa", text: "Apri la mappa per vedere le offerte intorno a te e trovare i negozi più vicini." },
      { sel: ["[data-testid=nav-coupons]"], title: "I tuoi coupon e risparmi", text: "Qui trovi i coupon che hai richiesto e quanto hai risparmiato in totale." },
    ],
  },
  vendor: {
    flag: "kn_tutorial_vendor_v1",
    slides: [
      { icon: "👋", title: "Benvenuto commerciante", text: "Pubblica offerte e attira nuovi clienti nel tuo negozio.", img: `${IMG}/vendor_1.jpg` },
      { icon: "➕", title: "Pubblica in 30 secondi", text: "Titolo, sconto e scadenza: puoi anche creare offerte ricorrenti.", img: `${IMG}/vendor_2.jpg` },
      { icon: "📷", title: "Valida in cassa", text: "Scansiona il QR del cliente o inserisci il codice per applicare lo sconto.", img: `${IMG}/vendor_3.jpg` },
      { icon: "📈", title: "Fidelizza e cresci", text: "Messaggi automatici, follower e storico degli incassi, tutto in un posto.", img: `${IMG}/vendor_4.jpg` },
    ],
    coach: [
      { sel: ["[data-testid=publish-cta]", "[data-testid=new-offer-btn]"], title: "Pubblica un'offerta", text: "Tocca qui per creare la tua offerta di oggi in pochi secondi." },
      { sel: ["[data-testid=vnav-validate]", "[data-testid=m-vnav-validate]"], title: "Valida in cassa", text: "Quando un cliente arriva, scansiona il QR o inserisci il suo codice qui." },
      { sel: ["[data-testid=vnav-store]", "[data-testid=m-vnav-store]"], title: "Impostazioni negozio", text: "Gestisci profilo, posizione, orari e messaggi automatici da qui." },
    ],
  },
};

export const tutorialDone = (role) => localStorage.getItem(DATA[role].flag) === "1";
export const markTutorialDone = (role) => localStorage.setItem(DATA[role].flag, "1");
export const resetTutorial = (role) => localStorage.removeItem(DATA[role].flag);

function findTarget(sels) {
  for (const s of sels) {
    const el = document.querySelector(s);
    if (el) { const r = el.getBoundingClientRect(); if (r.width > 0 && r.height > 0) return r; }
  }
  return null;
}

export default function OnboardingTutorial({ role, onClose }) {
  const cfg = DATA[role];
  const [phase, setPhase] = useState("carousel");
  const [i, setI] = useState(0);
  const [ci, setCi] = useState(0);
  const [rect, setRect] = useState(null);

  const finish = useCallback(() => { markTutorialDone(role); onClose(); }, [role, onClose]);

  // resolve coach target rect (skip steps whose target is missing)
  useLayoutEffect(() => {
    if (phase !== "coach") return;
    let idx = ci;
    let r = findTarget(cfg.coach[idx].sel);
    while (r === null && idx < cfg.coach.length - 1) { idx += 1; r = findTarget(cfg.coach[idx].sel); }
    if (r === null) { finish(); return; }
    if (idx !== ci) setCi(idx);
    setRect(r);
  }, [phase, ci, cfg, finish]);

  useEffect(() => {
    const onResize = () => { if (phase === "coach") { const r = findTarget(cfg.coach[ci].sel); if (r) setRect(r); } };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("scroll", onResize, true); };
  }, [phase, ci, cfg]);

  // ---- Carousel ----
  if (phase === "carousel") {
    const last = i === cfg.slides.length - 1;
    const cur = cfg.slides[i];
    return (
      <div className="fixed inset-0 z-[2000] bg-black overflow-hidden" data-testid="tutorial-carousel">
        {/* ---------- MOBILE: full-bleed illustration + bottom text ---------- */}
        <div className="md:hidden">
          <div className="absolute inset-0 flex h-full" style={{ width: `${cfg.slides.length * 100}%`, transform: `translateX(-${i * (100 / cfg.slides.length)}%)`, transition: "transform .45s cubic-bezier(0.22,1,0.36,1)" }}>
            {cfg.slides.map((s, k) => (
              <div key={k} className="relative h-full shrink-0" style={{ width: `${100 / cfg.slides.length}%` }}>
                <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.15) 100%)" }} />
                <div className="absolute inset-x-0 bottom-0 p-7 pb-40 text-white" style={{ opacity: k === i ? 1 : 0, transform: k === i ? "translateY(0)" : "translateY(12px)", transition: "opacity .4s .1s, transform .4s .1s" }}>
                  <h2 className="text-3xl font-extrabold leading-tight">{s.title}</h2>
                  <p className="text-white/85 text-lg mt-2 max-w-md">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="absolute inset-x-0 bottom-0 p-7 pb-10 z-10">
            <div className="flex items-center gap-2 mb-5">
              {cfg.slides.map((_, k) => <span key={k} className={`h-2 rounded-full transition-all ${k === i ? "w-6 bg-[#E63946]" : "w-2 bg-white/40"}`} />)}
            </div>
            <button data-testid="tutorial-next" onClick={() => (last ? setPhase("coach") : setI(i + 1))}
              className="w-full py-4 rounded-2xl kn-gradient text-white font-extrabold text-lg flex items-center justify-center gap-2">
              {last ? "Inizia" : "Avanti"} <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ---------- DESKTOP: split — illustration half + content half ---------- */}
        <div className="hidden md:flex absolute inset-0">
          <div className="relative h-full w-1/2 lg:w-[55%] bg-black overflow-hidden">
            {cfg.slides.map((s, k) => (
              <img key={k} src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover object-center"
                style={{ opacity: k === i ? 1 : 0, transition: "opacity .55s cubic-bezier(0.22,1,0.36,1)" }} />
            ))}
            <div className="absolute inset-y-0 right-0 w-28" style={{ background: "linear-gradient(to right, rgba(255,247,240,0) 0%, #FFF7F0 100%)" }} />
          </div>
          <div className="relative h-full w-1/2 lg:w-[45%] bg-[#FFF7F0] flex flex-col justify-center px-14 xl:px-24">
            <div className="flex items-center gap-2.5 mb-10">
              <div className="h-11 w-11 rounded-2xl kn-gradient grid place-items-center shadow-lg"><Ticket className="h-6 w-6 text-white" /></div>
              <span className="text-xl font-extrabold tracking-tight text-slate-900">KoupoNow</span>
            </div>
            <div key={i} className="kn-fade-up max-w-lg">
              <h2 className="text-4xl xl:text-5xl font-extrabold leading-[1.1] text-slate-900">{cur.title}</h2>
              <p className="text-lg xl:text-xl text-slate-600 mt-5 leading-relaxed">{cur.text}</p>
            </div>
            <div className="flex items-center gap-2 mt-12">
              {cfg.slides.map((_, k) => (
                <button key={k} aria-label={`Vai alla slide ${k + 1}`} data-testid={`tutorial-dot-${k}`} onClick={() => setI(k)}
                  className={`h-2.5 rounded-full transition-all ${k === i ? "w-7 bg-[#E63946]" : "w-2.5 bg-slate-300 hover:bg-slate-400"}`} />
              ))}
            </div>
            <button data-testid="tutorial-next-desktop" onClick={() => (last ? setPhase("coach") : setI(i + 1))}
              className="mt-8 self-start px-10 py-4 rounded-2xl kn-gradient text-white font-extrabold text-lg flex items-center gap-2 shadow-xl hover:brightness-105 transition">
              {last ? "Inizia" : "Avanti"} <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ---------- Shared Skip ---------- */}
        <button onClick={finish} data-testid="tutorial-skip" className="absolute top-5 right-5 z-20 flex items-center gap-1.5 font-semibold text-white rounded-full bg-white/15 backdrop-blur text-sm px-3 py-1.5 transition-colors hover:bg-white/25 md:text-base md:px-5 md:py-2.5 md:bg-white md:text-slate-700 md:shadow-lg md:ring-1 md:ring-slate-200 md:hover:bg-slate-50">
          <span>Salta</span>
          <X className="h-4 w-4 md:h-5 md:w-5" />
        </button>
      </div>
    );
  }

  // ---- Coach marks ----
  if (phase === "coach" && rect) {
    const step = cfg.coach[ci];
    const pad = 8;
    const holeTop = rect.top - pad, holeLeft = rect.left - pad, holeW = rect.width + pad * 2, holeH = rect.height + pad * 2;
    const vw = window.innerWidth, vh = window.innerHeight;
    const isDesktop = vw >= 768;
    const below = holeTop + holeH + 150 < vh;
    const tipTop = below ? holeTop + holeH + 12 : holeTop - 12;
    const last = ci === cfg.coach.length - 1;
    // On desktop anchor the tooltip beside the highlighted element; on mobile keep it edge-to-edge.
    let tipStyle;
    if (isDesktop) {
      const TW = 400, gap = 20;
      let left = holeLeft + holeW + gap;            // prefer to the right of the hole
      if (left + TW > vw - 20) left = holeLeft - gap - TW;   // else to the left
      if (left < 20) left = Math.min(Math.max(holeLeft, 20), vw - TW - 20); // fallback inside viewport
      let top = holeTop + holeH / 2 - 96;           // roughly vertically centered on the hole
      top = Math.min(Math.max(top, 20), vh - 240);
      tipStyle = { position: "fixed", top, left, width: TW };
    } else {
      tipStyle = { position: "fixed", top: below ? tipTop : undefined, bottom: below ? undefined : vh - tipTop, left: 16, right: 16, maxWidth: 380, margin: "0 auto" };
    }
    return (
      <div className="fixed inset-0 z-[2000]" data-testid="tutorial-coach">
        {/* spotlight hole via massive box-shadow */}
        <div
          style={{ position: "fixed", top: holeTop, left: holeLeft, width: holeW, height: holeH, borderRadius: 14, boxShadow: "0 0 0 9999px rgba(15,23,42,0.78)", transition: "all .25s" }}
          className="pointer-events-none ring-2 ring-white/80 md:ring-4 md:ring-white"
        />
        <div
          data-testid="tutorial-tooltip"
          style={tipStyle}
          className="kn-card p-4 md:p-6 kn-pop"
        >
          <p className="text-xs md:text-sm font-bold text-[#E63946]">{ci + 1} di {cfg.coach.length}</p>
          <h3 className="text-lg md:text-2xl font-extrabold text-slate-900 mt-0.5 md:mt-1">{step.title}</h3>
          <p className="text-slate-600 text-sm md:text-base mt-1 md:mt-2 md:leading-relaxed">{step.text}</p>
          <div className="flex items-center justify-between mt-3 md:mt-5">
            <button onClick={finish} data-testid="coach-skip" className="text-slate-400 hover:text-slate-600 font-semibold text-sm md:text-base transition-colors">Salta</button>
            <button onClick={() => (last ? finish() : setCi(ci + 1))} data-testid="coach-next" className="h-10 md:h-11 px-5 md:px-6 rounded-xl kn-gradient text-white font-bold text-sm md:text-base flex items-center gap-1.5">
              {last ? <>Ho capito <Check className="h-4 w-4" /></> : <>Avanti <ChevronRight className="h-4 w-4" /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
